import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MovieOrShowResponse, Suggestion } from "../types/interfaces";
import { API_BASE_URL } from "../config";
import { useSuggestions } from "../hooks/useSuggestions";

export default function Home() {
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState("");
    const [apiResponse, setApiResponse] = useState("");
    const [searchFoundNothing, setSearchFoundNothing] = useState(false);
    // Suppressed after a pick or a submit, so the dropdown does not reopen over
    // the value it just filled in.
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const envRef = useRef<string>(""); // Initialize with an empty string
    const searchContainerRef = useRef<HTMLDivElement>(null);

    const { suggestions, clearSuggestions } = useSuggestions(inputValue, suggestionsOpen);

    useEffect(() => {
        envRef.current = process.env.REACT_APP_ENV || "dev"; // Default to dev if not set
        console.log(`Running in ${envRef.current} mode`);
    }, []);

    // A click outside the search box dismisses the dropdown.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!searchContainerRef.current?.contains(event.target as Node)) {
                setSuggestionsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Any change to the list invalidates whatever was highlighted.
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [suggestions]);

    const showSuggestions = suggestionsOpen && suggestions.length > 0;

    // Suggestions already carry the Watchmode id, so picking one skips the
    // results page and the search request behind it entirely.
    const handleSuggestionSelect = (suggestion: Suggestion) => {
        setInputValue(suggestion.title);
        setSuggestionsOpen(false);
        clearSuggestions();
        navigate(`/movieShowDetailsPage/${suggestion.id}`);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions) return;

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % suggestions.length);
                break;
            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
                break;
            case "Enter":
                // Only intercept Enter when something is actually highlighted;
                // otherwise fall through to a normal full search.
                if (highlightedIndex >= 0) {
                    e.preventDefault();
                    handleSuggestionSelect(suggestions[highlightedIndex]);
                }
                break;
            case "Escape":
                setSuggestionsOpen(false);
                break;
            default:
                break;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); // Prevent the default form submission behavior
        setSuggestionsOpen(false);
        // Clear the last verdict, so a failed second search does not sit under
        // "nothing found" from the first.
        setSearchFoundNothing(false);
        try {
            // Construct the URL with query parameters
            const res = await fetch(
                `${API_BASE_URL}/api/titles/${encodeURIComponent(inputValue)}`,
                {
                    method: "GET",
                }
            );

            if (res.ok) {
                const data = await res.json();
                const movieOrShowResponse: MovieOrShowResponse = data;
                setApiResponse(data?.message);
                const results = movieOrShowResponse?.results || [];
                // One search answers two questions, so a query that matches
                // nobody's idea of a title can still match an actor.
                const people = movieOrShowResponse?.people || [];
                setSearchFoundNothing(results.length === 0 && people.length === 0);
                if (results.length > 0 || people.length > 0) {
                    // The back-end corrects a misspelled search against its local
                    // title index. Pass that along so the results page can say so
                    // rather than silently answering a different question.
                    navigate("/titles-page", {
                        state: {
                            results,
                            people,
                            didYouMean: movieOrShowResponse?.did_you_mean,
                            correctedFrom: movieOrShowResponse?.corrected_from,
                        },
                    });
                }
            } else {
                setApiResponse("Error: Unable to fetch data from API");
            }
        } catch (error) {
            console.error("Error:", error);
            setApiResponse("Error: Network or server issue");
        }
    };

    return (
        <div className="container">
            <main className="main">
                <div className="content">
                    <img
                        src="/topcat-face.png"
                        alt=""
                        className="brand-mark"
                    />
                    <div className="brand-copy">
                        <h1 className="brand-title">TopCat</h1>
                        <p className="brand-tagline">
                            Find where a movie or TV show is streaming, by region —
                            or look up an actor and search their films.
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className='form'>
                        <label htmlFor="search-bar">Search a movie, TV show or actor</label>
                        <div className="search-container" ref={searchContainerRef}>
                            <input
                                className="search-bar"
                                id="search-bar"
                                type="text"
                                value={inputValue}
                                onChange={(e) => {
                                    setInputValue(e.target.value);
                                    setSuggestionsOpen(true);
                                }}
                                onFocus={() => setSuggestionsOpen(true)}
                                onKeyDown={handleKeyDown}
                                placeholder="The Godfather, or Al Pacino"
                                autoComplete="off"
                                role="combobox"
                                aria-expanded={showSuggestions}
                                aria-controls="search-suggestions"
                                aria-autocomplete="list"
                                aria-activedescendant={
                                    highlightedIndex >= 0
                                        ? `suggestion-${suggestions[highlightedIndex]?.id}`
                                        : undefined
                                }
                            />
                            <button type="submit" className="submit-button">
                                Search
                            </button>

                            {showSuggestions && (
                                <ul className="suggestions-list" id="search-suggestions" role="listbox">
                                    {suggestions.map((suggestion, index) => (
                                        <li
                                            key={suggestion.id}
                                            id={`suggestion-${suggestion.id}`}
                                            role="option"
                                            aria-selected={index === highlightedIndex}
                                            className={
                                                index === highlightedIndex
                                                    ? "suggestion-item suggestion-item-active"
                                                    : "suggestion-item"
                                            }
                                            // mousedown, not click: the input's blur would
                                            // otherwise close the list before the click lands.
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleSuggestionSelect(suggestion);
                                            }}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                        >
                                            <span className="suggestion-title">{suggestion.title}</span>
                                            {suggestion.year ? (
                                                <span className="suggestion-meta">{suggestion.year}</span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </form>
                    {apiResponse && <p className="error-message">{apiResponse}</p>}
                    {searchFoundNothing && (
                        <p className="status-message">
                            No movie, TV show or person found. Try fewer words, or check
                            the spelling.
                        </p>
                    )}
                </div>
            </main>
        </div>
    );
}
